// Maker-checker for employee master data (Model A). Approvers review pending
// change requests (old → new per field), approve/reject with a note, and the
// tenant owner configures which fields are gated. Managers get read access.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClipboardCheck, Loader2, ShieldAlert, Check, X, Ban, Settings2, Save, User, CalendarClock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Switch } from '@/features/shared/components/ui/switch';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import toast from 'react-hot-toast';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { useHasPermission } from '@/rbac/useRbac';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { getAllEmployees } from '@/lib/hrApi';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  listChangeRequests, approveChangeRequest, rejectChangeRequest, cancelChangeRequest,
  getApprovalPolicy, updateApprovalPolicy, humanizeField,
  type ChangeRequest, type ChangeRequestFilter, type ApprovalPolicy,
} from '@/services/changeRequestService';

const FILTERS: ChangeRequestFilter[] = ['pending', 'approved', 'rejected', 'cancelled', 'all'];

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-warning-soft text-warning-strong border-transparent',
  approved: 'bg-success-soft text-success-strong border-transparent',
  rejected: 'bg-danger-soft text-danger-strong border-transparent',
  cancelled: 'bg-muted text-muted-foreground border-border',
};

const fmtDateTime = (d?: string | null) =>
  d
    ? new Date(d).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

const showVal = (v: unknown) => (v == null || v === '' ? '—' : String(v));

const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

// ---------------------------------------------------------------- request card
const RequestCard: React.FC<{
  req: ChangeRequest;
  nameOf: (id?: string | null) => string;
  canApprove: boolean;
  onDecided: () => void;
}> = ({ req, nameOf, canApprove, onDecided }) => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject' | 'cancel' | null>(null);

  const act = async (kind: 'approve' | 'reject' | 'cancel') => {
    if (kind === 'reject') {
      const ok = await confirm({
        title: t('changes.reject_title', { defaultValue: 'Reject change request' }),
        message: t('changes.reject_confirm', { defaultValue: 'Reject the requested changes for {{name}}?', name: nameOf(req.employee_id) }),
        confirmLabel: t('changes.reject', { defaultValue: 'Reject' }),
        danger: true,
      });
      if (!ok) return;
    }
    if (kind === 'cancel') {
      const ok = await confirm({
        title: t('changes.cancel_title', { defaultValue: 'Cancel change request' }),
        message: t('changes.cancel_confirm', { defaultValue: 'Cancel this change request? The proposed changes will be discarded.' }),
        confirmLabel: t('changes.cancel_request', { defaultValue: 'Cancel request' }),
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(kind);
    try {
      if (kind === 'approve') {
        await approveChangeRequest(req.id, note);
        toast.success(t('changes.approved_toast', { defaultValue: 'Changes approved and applied.' }));
      } else if (kind === 'reject') {
        await rejectChangeRequest(req.id, note);
        toast.success(t('changes.rejected_toast', { defaultValue: 'Change request rejected.' }));
      } else {
        await cancelChangeRequest(req.id);
        toast.success(t('changes.cancelled_toast', { defaultValue: 'Change request cancelled.' }));
      }
      onDecided();
    } catch (e: any) {
      // Surfaces the backend's 403 self-approval message and 409 already-decided.
      toast.error(getApiErrorMessage(e, errMsg(e, t('changes.action_failed', { defaultValue: 'Could not complete the action.' }))));
      if (e?.response?.status === 409) onDecided(); // someone else decided — refresh
    } finally {
      setBusy(null);
    }
  };

  const fields = Object.entries(req.changes || {});
  const isPending = req.status === 'pending';

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground flex items-center gap-2">
              <User className="w-4 h-4 text-orange-500" /> {nameOf(req.employee_id)}
            </p>
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
              <span>
                {t('changes.requested_by', { defaultValue: 'Requested by' })}{' '}
                <span className="text-foreground">{nameOf(req.requested_by)}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> {fmtDateTime(req.created_at)}
              </span>
            </p>
            {req.reason && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('changes.reason', { defaultValue: 'Reason' })}: <span className="text-foreground">{req.reason}</span>
              </p>
            )}
          </div>
          <Badge className={`border capitalize ${STATUS_BADGE[req.status] || STATUS_BADGE.cancelled}`}>
            {req.status}
          </Badge>
        </div>

        {/* Field-by-field diff */}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-left text-xs text-muted-foreground">
                <th className="py-2 px-3 font-medium">{t('changes.field', { defaultValue: 'Field' })}</th>
                <th className="py-2 px-3 font-medium">{t('changes.current', { defaultValue: 'Current' })}</th>
                <th className="py-2 px-3 font-medium">{t('changes.proposed', { defaultValue: 'Proposed' })}</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(([field, ch]) => (
                <tr key={field} className="border-t border-border align-top">
                  <td className="py-2 px-3 font-medium text-foreground whitespace-nowrap">{humanizeField(field)}</td>
                  <td className="py-2 px-3 text-muted-foreground break-all">{showVal(ch?.old)}</td>
                  <td className="py-2 px-3 text-foreground break-all">{showVal(ch?.new)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Decision trail (decided requests) */}
        {!isPending && (req.decided_by || req.decision_note) && (
          <p className="text-xs text-muted-foreground">
            {t('changes.decided_by', { defaultValue: 'Decided by' })}{' '}
            <span className="text-foreground">{nameOf(req.decided_by)}</span>
            {req.decided_at ? ` · ${fmtDateTime(req.decided_at)}` : ''}
            {req.decision_note ? ` · “${req.decision_note}”` : ''}
          </p>
        )}

        {/* Actions */}
        {isPending && (
          <div className="space-y-2">
            {canApprove && (
              <input
                className="w-full h-10 px-3 rounded-md border border-border bg-card text-sm"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('changes.note_ph', { defaultValue: 'Decision note (optional)' })}
                aria-label={t('changes.note_ph', { defaultValue: 'Decision note (optional)' })}
              />
            )}
            <div className="flex flex-wrap items-center gap-2">
              {canApprove && (
                <>
                  <Button onClick={() => act('approve')} disabled={busy !== null}>
                    {busy === 'approve' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    {t('changes.approve', { defaultValue: 'Approve' })}
                  </Button>
                  <Button variant="outline" className="text-danger border-red-200 hover:bg-red-50 dark:border-red-500/25"
                    onClick={() => act('reject')} disabled={busy !== null}>
                    {busy === 'reject' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
                    {t('changes.reject', { defaultValue: 'Reject' })}
                  </Button>
                </>
              )}
              <Button variant="ghost" className="text-muted-foreground" onClick={() => act('cancel')} disabled={busy !== null}>
                {busy === 'cancel' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                {t('changes.cancel_request', { defaultValue: 'Cancel request' })}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- policy card
const PolicyCard: React.FC = () => {
  const { t } = useTranslation('admin');
  const [policy, setPolicy] = useState<ApprovalPolicy | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [fields, setFields] = useState<string[]>([]);
  const [hrOnly, setHrOnly] = useState<string[]>([]);
  const [managerApproves, setManagerApproves] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const applyPolicy = (p: ApprovalPolicy) => {
    setPolicy(p);
    setEnabled(!!p.enabled);
    setFields(p.fields || []);
    setHrOnly(p.hr_only_fields || []);
    setManagerApproves(p.manager_approves !== false);
  };

  useEffect(() => {
    let alive = true;
    getApprovalPolicy()
      .then((p) => { if (alive) applyPolicy(p); })
      .catch(() => { if (alive) setPolicy(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const toggleField = (f: string, on: boolean) =>
    setFields((cur) => (on ? Array.from(new Set([...cur, f])) : cur.filter((x) => x !== f)));

  const toggleHrOnly = (f: string, on: boolean) =>
    setHrOnly((cur) => (on ? Array.from(new Set([...cur, f])) : cur.filter((x) => x !== f)));

  const sameSet = (a: string[], b: string[]) =>
    JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

  const dirty = !!policy && (
    enabled !== !!policy.enabled
    || !sameSet(fields, policy.fields || [])
    || !sameSet(hrOnly, policy.hr_only_fields || [])
    || managerApproves !== (policy.manager_approves !== false)
  );

  const save = async () => {
    setSaving(true);
    try {
      const p = await updateApprovalPolicy({
        enabled, fields, hr_only_fields: hrOnly, manager_approves: managerApproves,
      });
      applyPolicy(p);
      toast.success(t('changes.policy_saved', { defaultValue: 'Approval policy saved.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('changes.policy_save_failed', { defaultValue: 'Could not save the policy.' }))));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-orange-100 dark:border-orange-500/20">
        <CardContent className="py-8 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('changes.policy_loading', { defaultValue: 'Loading policy…' })}
        </CardContent>
      </Card>
    );
  }
  if (!policy) return null; // policy endpoint unavailable / not permitted

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          <Settings2 className="w-4 h-4" /> {t('changes.policy_title', { defaultValue: 'Approval policy' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={setEnabled}
            aria-label={t('changes.policy_enabled', { defaultValue: 'Require approval for changes' })} />
          <span className="text-sm text-foreground">
            {t('changes.policy_enabled', { defaultValue: 'Require approval for changes' })}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('changes.policy_hint', { defaultValue: 'When enabled, changes to these fields by non-approvers must be approved first.' })}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {(policy.tracked_fields || []).map((f) => (
            <label key={f} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <Checkbox checked={fields.includes(f)} disabled={!enabled}
                onCheckedChange={(v) => toggleField(f, v === true)} />
              {humanizeField(f)}
            </label>
          ))}
        </div>

        {/* ---- routing: who approves (managers vs HR) ---- */}
        <div className="border-t border-orange-100 dark:border-orange-500/20 pt-4 space-y-4">
          <div>
            <p className="text-sm font-medium text-foreground">
              {t('changes.routing_title', { defaultValue: 'Who approves' })}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('changes.routing_hint', {
                defaultValue:
                  'Line managers only ever see requests for their own reporting line. Choose which changes go to HR instead of managers.',
              })}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={managerApproves} disabled={!enabled} onCheckedChange={setManagerApproves}
              aria-label={t('changes.manager_approves', { defaultValue: 'Line managers can approve their team’s changes' })} />
            <span className="text-sm text-foreground">
              {t('changes.manager_approves', { defaultValue: 'Line managers can approve their team’s changes' })}
            </span>
          </div>
          {!managerApproves && (
            <p className="text-xs text-muted-foreground -mt-2">
              {t('changes.manager_approves_off_hint', { defaultValue: 'All change approvals will route to HR.' })}
            </p>
          )}

          {managerApproves && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('changes.hr_only_hint', {
                  defaultValue:
                    'These fields (and any change with a supporting document) always go to HR, never a line manager. Statutory identity fields are included by default.',
                })}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {(policy.tracked_fields || []).map((f) => (
                  <label key={`hr-${f}`} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <Checkbox checked={hrOnly.includes(f)} disabled={!enabled}
                      onCheckedChange={(v) => toggleHrOnly(f, v === true)} />
                    {humanizeField(f)}
                    {(policy.statutory_fields || []).includes(f) && (
                      <span className="text-[10px] uppercase tracking-wide text-orange-600 dark:text-orange-400">
                        {t('changes.statutory_tag', { defaultValue: 'statutory' })}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <Button onClick={save} disabled={!dirty || saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {t('changes.policy_save', { defaultValue: 'Save policy' })}
        </Button>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- page
export const ChangeApprovalsPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const canApprove = useHasPermission('employee:change:approve');
  const canEditPolicyPerm = useHasPermission('tenant:update:own');
  const { isManager, isTenantOwner } = useRoleAccess();
  const canView = canApprove || isManager;
  const canEditPolicy = isTenantOwner || canEditPolicyPerm;

  const [filter, setFilter] = useState<ChangeRequestFilter>('pending');
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [names, setNames] = useState<Record<string, string>>({});

  // Directory lookup: employee UUID → display name (same pattern as Attendance).
  useEffect(() => {
    if (!canView) return;
    let alive = true;
    getAllEmployees()
      .then((emps) => {
        if (!alive) return;
        const map: Record<string, string> = {};
        emps.forEach((e) => {
          map[e.id] = `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || e.id;
          // Requested-by/decided-by may be recorded as the auth user id.
          if ((e as any).user_id) map[(e as any).user_id] = map[e.id];
        });
        setNames(map);
      })
      .catch(() => { /* directory unavailable — fall back to raw ids */ });
    return () => { alive = false; };
  }, [canView]);

  const nameOf = useCallback(
    (id?: string | null) => (id ? names[id] || id : '—'),
    [names],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRequests(await listChangeRequests(filter));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('changes.load_failed', { defaultValue: 'Could not load change requests.' }))));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => { if (canView) void load(); }, [canView, load]);

  const filterLabel = useMemo<Record<ChangeRequestFilter, string>>(() => ({
    pending: t('changes.filter_pending', { defaultValue: 'Pending' }),
    approved: t('changes.filter_approved', { defaultValue: 'Approved' }),
    rejected: t('changes.filter_rejected', { defaultValue: 'Rejected' }),
    cancelled: t('changes.filter_cancelled', { defaultValue: 'Cancelled' }),
    all: t('changes.filter_all', { defaultValue: 'All' }),
  }), [t]);

  if (!canView) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">{t('changes.no_access_title', { defaultValue: 'Managers and approvers only' })}</h1>
        <p className="text-muted-foreground mt-1">
          {t('changes.no_access_body', { defaultValue: 'You don’t have permission to view change approvals.' })}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal flex items-center gap-2">
          <ClipboardCheck className="w-6 h-6" /> {t('changes.title', { defaultValue: 'Change Approvals' })}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('changes.subtitle', { defaultValue: 'Review and approve sensitive employee data changes before they take effect.' })}
        </p>
      </div>

      {/* Policy (tenant owner) */}
      {canEditPolicy && <PolicyCard />}

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`h-8 px-3 rounded-full border text-sm transition-colors ${
              filter === f
                ? 'bg-orange-500 border-orange-500 text-white'
                : 'bg-card border-border text-foreground hover:border-orange-300 dark:hover:border-orange-500/40'
            }`}
          >
            {filterLabel[f]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('changes.loading', { defaultValue: 'Loading change requests…' })}
        </div>
      ) : requests.length === 0 ? (
        <Card className="border-orange-100 dark:border-orange-500/20">
          <CardContent className="py-12 text-center text-muted-foreground">
            {filter === 'pending'
              ? t('changes.empty_pending', { defaultValue: 'No pending change requests. You’re all caught up.' })
              : t('changes.empty', { defaultValue: 'No change requests match this filter.' })}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((r) => (
            <RequestCard key={r.id} req={r} nameOf={nameOf} canApprove={canApprove} onDecided={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ChangeApprovalsPage;
